import React, { useMemo } from 'react';
import {
    collectRoiIds,
    getXraysMappedToActiveJoints,
} from 'helpers/helpers';
import ErrorPage from '../ErrorPage/ErrorPage';
import { useAuth } from 'hooks/useAuth';

import { ExamRoomContent } from 'components/organisms/ExamRoom/ExamRoomContent';
import { useAppointmentData } from 'hooks/useAppointmentData';
import { useAppointmentParamsFromQuery } from 'hooks/useAppointmentParamsFromQuery';
import { useXrayData } from 'hooks/useXrayData';
import { useProData } from 'hooks/useProData';
import { useRoiImageData } from 'hooks/useRoiImageData';
import { useAppointmentRequiredJointViewsData } from 'hooks/useAppointmentJointViewsData';
import { useXrayMutations } from 'hooks/useXrayMutations';
import { useActivePatient } from 'hooks/useActivePatient';
import TriageFlowLayout from 'components/templates/TriageFlowLayout/TriageFlowLayout';

const ExamRoomAllJointsPage: React.FC = () => {

    const { user } = useAuth();

    const { activePatient } = useActivePatient();

    const {
        appointmentId,
        appointmentDateISOString,
        flow
    } = useAppointmentParamsFromQuery();

    const {
        appointment,
        appointmentError,
        appointmentLoading,
    } = useAppointmentData({
        selectedDateISOString: appointmentDateISOString,
        appointmentId,
        preventRefetch: true,
    });

    const {
        appointmentRequiredJointViews,
    } = useAppointmentRequiredJointViewsData({
        selectedDateISOString: appointmentDateISOString,
        appointmentId,
    })

    const {
        latestProsMappedToJoints,
        allAppointmentProsLoading,
        allAppointmentProsError,
    } = useProData({ appointment, options: { preventRefetch: true } });

    const {
        xraysMappedToJoints,
        allAppointmentXraysLoading,
        allAppointmentXraysError,
    } = useXrayData({ appointment });

    const xraysMappedToActiveJoints = useMemo(() => getXraysMappedToActiveJoints(appointment, xraysMappedToJoints), [appointment, xraysMappedToJoints]);

    const allActiveJointXRays = useMemo(() => {
        return Object.values(xraysMappedToActiveJoints).flat();
    }, [xraysMappedToActiveJoints]);

    const roiIds = useMemo(() => allActiveJointXRays ? collectRoiIds(allActiveJointXRays) : null, [allActiveJointXRays]);

    const { roiImgUrls, roiImagesLoading } = useRoiImageData({
        roiIds,
        options: { preventRefetch: true },
    });

    const initalQueriesLoading = appointmentLoading || allAppointmentProsLoading || allAppointmentXraysLoading;
    const isError = appointmentError || allAppointmentProsError || allAppointmentXraysError;

    const { batchConfirmXrays, updateXrayLoading } = useXrayMutations({ appointment });

    if (!initalQueriesLoading && !appointment) return (
        <ErrorPage code={404} />
    );

    if (isError) return (
        <ErrorPage />
    );

    return (
        <TriageFlowLayout title="Exam Room" singlePatient={activePatient}>
            <ExamRoomContent {...{
                user,
                appointment,
                xraysMappedToJoints,
                latestProsMappedToJoints,
                roiImgUrls,
                appointmentRequiredJointViews,
                batchConfirmXrays,
                activePatient,
                flow,
                initalQueriesLoading,
                updateXrayLoading,
                roiImagesLoading
            }} />
        </TriageFlowLayout>
    );
};

export default ExamRoomAllJointsPage;
